import { Box, Button, FormControl, InputAdornment, InputLabel, MenuItem, Select, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs, TextField, Typography } from '@mui/material';
import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import AddEzvizCameraModal from '../components/AddEzvizCameraModal';
import { Brand, Camera, User } from '../types';
import { supabase } from '../utils/supabase';
import ManageUserTable from './ManageUserTable';

// Helper component for tab panels.
interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}
function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`admin-tabpanel-${index}`}
            aria-labelledby={`admin-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

export default function AdminPanel() {
    // State for tabs (0: Manage Users, 1: Manage Cameras, 2: Manage Brand)
    const [tab, setTab] = useState(0);
    const [users, setUsers] = useState<User[]>([]);
    const [cameras, setCameras] = useState<Camera[]>([]);
    const [newUser, setNewUser] = useState<User>({ email: '', password: '', role: 'customer', camera_ids: [], layout: null, columns: null, notes: null });
    const [newCamera, setNewCamera] = useState<Camera>({ id: '', name: '', stream_url: '' });
    const [brand, setBrand] = useState<Brand | null>(null);
    const [brandColor, setBrandColor] = useState<string>('');
    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [loading, setLoading] = useState({
        addUser: false,
        deleteUser: new Map<string, boolean>(),
        updateUser: false,
        addCamera: false,
        deleteCamera: new Map<string, boolean>(),
        updateBrand: false,
    });
    const [ezvizModalOpen, setEzvizModalOpen] = useState(false);
    const supabaseAPI: string = `${process.env.SUPABASE_URL}/functions/v1`;

    // Handlers for tab switching.
    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTab(newValue);
    };

    useEffect(() => {
        fetchUsers();
        fetchCameras();
        fetchBrand();
    }, []);

    async function fetchUsers() {
        const { data: session } = await supabase.auth.getSession();
        console.log('JWT claims:', session?.session?.access_token ? JSON.parse(atob(session.session.access_token.split('.')[1])) : 'No session');
        const { data, error } = await supabase.from('users').select('id, email, role, users_cameras (camera_id), layout, notes');
        if (error) {
            console.error('Fetch users error:', error);
            return;
        }

        const usersWithCameraIds = data.map((user: any) => ({
            ...user,
            camera_ids: user.users_cameras.map((item: any) => item.camera_id),
            users_cameras: undefined,
        }));
        console.log('Users fetched:', data);
        setUsers(usersWithCameraIds);
    }

    async function fetchCameras() {
        const { data, error } = await supabase.from('cameras').select('*');
        if (error) {
            console.error('Fetch cameras error:', error);
            return;
        }
        console.log('Cameras fetched:', data);
        setCameras(data);
    }

    async function fetchBrand() {
        const { data, error } = await supabase.from('brand').select('*').limit(1).single();
        if (error) {
            console.error('Fetch brand error:', error);
            return;
        }
        console.log('Brand fetched:', data);
        setBrand(data);
        setBrandColor(data?.color.replace('#', '') || '');
    }

    async function getUserAccessToken() {
        const { data: { session } } = await supabase.auth.getSession();
        return session?.access_token;
    }

    async function createUser(e: FormEvent) {
        e.preventDefault();
        setLoading((prev) => ({ ...prev, addUser: true }));
        try {
            const accessToken = await getUserAccessToken();
            if (!accessToken) {
                console.error('No user session found');
                return;
            }

            const response = await fetch(`${supabaseAPI}/createUser`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`,
                },
                body: JSON.stringify({
                    email: newUser.email,
                    password: newUser.password,
                    role: newUser.role,
                }),
            });
            const result = await response.json();
            if (result.error) {
                console.error('Create user error:', result.error);
                return;
            }
            setNewUser({ email: '', password: '', role: 'customer', camera_ids: [], layout: null, columns: null, notes: null });
            fetchUsers();
        } catch (error) {
            console.error('Error creating user:', error);
        } finally {
            setLoading((prev) => ({ ...prev, addUser: false }));
        }
    }

    async function updateUser(user: User) {
        setLoading((prev) => ({ ...prev, updateUser: true }));
        try {
            const accessToken = await getUserAccessToken();
            if (!accessToken) {
                console.error('No user session found');
                return;
            }

            console.log('Updating user with data:', {
                id: user.id,
                email: user.email,
                role: user.role,
                notes: user.notes
            });

            // Update user in the users table
            const { data, error } = await supabase
                .from('users')
                .update({
                    email: user.email,
                    role: user.role,
                    notes: user.notes
                })
                .eq('id', user.id)
                .select()
                .single();

            if (error) {
                console.error('Error updating user:', error);
                return;
            }
            console.log('User updated:', data);

            // Update camera associations for customers
            if (user.role === 'customer') {
                // Delete existing camera associations
                const { error: deleteError } = await supabase
                    .from('users_cameras')
                    .delete()
                    .eq('user_id', user.id);
                if (deleteError) {
                    console.error('Error removing old camera associations:', deleteError);
                    return;
                }

                // Insert new camera associations
                if (user.camera_ids && user.camera_ids.length > 0) {
                    const cameraInserts = user.camera_ids.map((camera_id) => ({
                        user_id: user.id,
                        camera_id,
                    }));
                    const { error: insertError } = await supabase
                        .from('users_cameras')
                        .insert(cameraInserts);
                    if (insertError) {
                        console.error('Error assigning cameras:', insertError);
                        return;
                    }
                }
            }

            // If password is provided, update it via server-side endpoint
            if (user.password) {
                const response = await fetch(`${supabaseAPI}/updateUser`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify({ id: user.id, password: user.password }),
                });
                const result = await response.json();
                if (result.error) {
                    console.error('Error updating password:', result.error);
                    return;
                }
                console.log('Password updated successfully');
            }
            fetchUsers();

            // sign out for changing own password
            const currentUser = await supabase.auth.getUser();
            if (currentUser.data.user?.id === user.id) {
                await supabase.auth.signOut();
            }
        } catch (error) {
            console.error('Error updating user:', error);
        } finally {
            setLoading((prev) => ({ ...prev, updateUser: false }));
        }
    }

    async function deleteUser(id: string) {
        setLoading((prev) => ({
            ...prev,
            deleteUser: new Map(prev.deleteUser).set(id, true),
        }));
        try {
            const accessToken = await getUserAccessToken();
            if (!accessToken) {
                console.error('No user session found');
                return;
            }

            const response = await fetch(`${supabaseAPI}/deleteUser`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`,
                },
                body: JSON.stringify({ id }),
            });
            const result = await response.json();
            if (result.error) {
                console.error('Error deleting user:', result.error);
                return;
            }
            fetchUsers();
        } catch (error) {
            console.error('Error deleting user:', error);
        } finally {
            setLoading((prev) => {
                const newDeleteUser = new Map(prev.deleteUser);
                newDeleteUser.delete(id);
                return { ...prev, deleteUser: newDeleteUser };
            });
        }
    }

    async function createCamera(e: FormEvent) {
        e.preventDefault();
        setLoading((prev) => ({ ...prev, addCamera: true }));
        try {
            const { data, error } = await supabase.from('cameras').insert({
                name: newCamera.name,
                stream_url: newCamera.stream_url,
            }).select().single();
            if (error) {
                console.error('Error creating camera:', error);
                return;
            }
            console.log('Camera created:', data);
            setNewCamera({ id: '', name: '', stream_url: '' });
            fetchCameras();
        } catch (error) {
            console.error('Error creating camera:', error);
        } finally {
            setLoading((prev) => ({ ...prev, addCamera: false }));
        }
    }

    async function deleteCamera(id: string) {
        setLoading((prev) => ({
            ...prev,
            deleteCamera: new Map(prev.deleteCamera).set(id, true),
        }));
        try {
            const accessToken = await getUserAccessToken();
            if (!accessToken) {
                console.error('No user session found');
                return;
            }

            // removing camera from public:cameras should cascade to the users_cameras table,
            // but do it here again in case it fails
            const { error: deleteAssocError } = await supabase
                .from('users_cameras')
                .delete()
                .eq('camera_id', id);
            if (deleteAssocError) {
                console.error('deleteCamera: Error removing association: ', deleteAssocError);
                return;
            }

            // Proceed with deletion
            const { error } = await supabase
                .from('cameras')
                .delete()
                .eq('id', id);

            if (error) {
                console.error('Error deleting camera:', error);
                return;
            }
            console.log('Camera deleted:', id);
            fetchCameras();
        } catch (error) {
            console.error('Error deleting camera:', error);
        } finally {
            setLoading((prev) => {
                const newDeleteCamera = new Map(prev.deleteCamera);
                newDeleteCamera.delete(id);
                return { ...prev, deleteCamera: newDeleteCamera };
            });
        }
    }

    async function updateBrand(e: FormEvent) {
        e.preventDefault();
        setLoading((prev) => ({ ...prev, updateBrand: true }));
        try {
            // Handle logo upload with fixed filename
            if (logoFile) {
                let fileExt = logoFile.name.split('.').pop();
                if (fileExt) {
                    fileExt = fileExt.toLowerCase();
                }
                const fileName = `brand_logo.${fileExt}`; // Fixed filename
                const { error: uploadError } = await supabase.storage
                    .from('image')
                    .upload(fileName, logoFile, { upsert: true });

                if (uploadError) {
                    console.error('Error uploading logo:', uploadError);
                    return;
                }
            }

            // Upsert brand record (no logo_path)
            const { data, error } = await supabase
                .from('brand')
                .upsert({
                    id: brand?.id || '00000000-0000-0000-0000-000000000001', // Fixed ID for single record
                    color: `#${brandColor}`,
                })
                .select()
                .single();

            if (error) {
                console.error('Error updating brand:', error);
                return;
            }
            console.log('Brand updated:', data);
            setBrand(data);
            setLogoFile(null);
            fetchBrand();
        } catch (error) {
            console.error('Error updating brand:', error);
        } finally {
            setLoading((prev) => ({ ...prev, updateBrand: false }));
        }
    }

    // Handle file input change
    const handleLogoChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setLogoFile(e.target.files[0]);
        }
    };

    // Get public URL for logo
    const getLogoUrl = () => {
        const { data } = supabase.storage.from('image').getPublicUrl('brand_logo.png');
        return data.publicUrl;
    };

    return (
        <Box>
            <Tabs value={tab} onChange={handleTabChange} aria-label="admin panel tabs" sx={{ mb: 3 }}>
                <Tab label="Manage Users" />
                <Tab label="Manage Cameras" />
                <Tab label="Manage Brand" />
            </Tabs>

            {/* Manage Users Tab */}
            <TabPanel value={tab} index={0}>
                <Typography variant="h6">Manage Users</Typography>
                <Box component="form" onSubmit={createUser} sx={{ my: 2 }}>
                    <TextField
                        label="Email"
                        value={newUser.email}
                        onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                        sx={{ mr: 2 }}
                    />
                    <TextField
                        label="Password"
                        type="password"
                        value={newUser.password}
                        onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                        sx={{ mr: 2 }}
                    />
                    <FormControl sx={{ mr: 2 }}>
                        <InputLabel>Role</InputLabel>
                        <Select
                            value={newUser.role}
                            label="Role"
                            onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                        >
                            <MenuItem value="customer">Customer</MenuItem>
                            <MenuItem value="admin">Admin</MenuItem>
                        </Select>
                    </FormControl>
                    <Button
                        type="submit"
                        variant="contained"
                        loading={loading.addUser}
                    >
                        Add User
                    </Button>
                </Box>
                <ManageUserTable
                    users={users}
                    cameras={cameras}
                    deleteUser={deleteUser}
                    loading={loading}
                    updateUser={updateUser}
                />
            </TabPanel>

            {/* Manage Cameras Tab */}
            <TabPanel value={tab} index={1}>
                <Typography variant="h6">Manage Cameras</Typography>
                <Box component="form" onSubmit={createCamera} sx={{ my: 2 }}>
                    <TextField
                        label="Camera Name"
                        value={newCamera.name}
                        onChange={(e) => setNewCamera({ ...newCamera, name: e.target.value })}
                        sx={{ mr: 2 }}
                    />
                    <TextField
                        label="Stream URL"
                        value={newCamera.stream_url}
                        onChange={(e) => setNewCamera({ ...newCamera, stream_url: e.target.value })}
                        sx={{ mr: 2 }}
                    />
                    <Button
                        type="submit"
                        variant="contained"
                        loading={loading.addCamera}
                    >
                        Add Camera
                    </Button>
                    <Button
                        type="button"
                        variant="contained"
                        color="warning"
                        sx={{ ml: 2 }}
                        onClick={() => setEzvizModalOpen(true)}
                    >
                        EZVIZ
                    </Button>
                </Box>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Stream URL</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {cameras.map((camera) => (
                            <TableRow key={camera.id}>
                                <TableCell>{camera.name}</TableCell>
                                <TableCell>{camera.stream_url}</TableCell>
                                <TableCell>
                                    <Button
                                        onClick={() => deleteCamera(camera.id)}
                                        loading={loading.deleteCamera.get(camera.id) || false}
                                    >
                                        Delete
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                <AddEzvizCameraModal
                    open={ezvizModalOpen}
                    onClose={() => setEzvizModalOpen(false)}
                    setNewCamera={setNewCamera}
                />
            </TabPanel>

            {/* Manage Brand Tab */}
            <TabPanel value={tab} index={2}>
                <Typography variant="h6">Manage Brand</Typography>
                <Box component="form" onSubmit={updateBrand} sx={{ my: 2 }}>
                    <TextField
                        label="Brand Color (Hex)"
                        value={brandColor}
                        onChange={(e) => setBrandColor(e.target.value)}
                        placeholder="FF5733"
                        sx={{ mr: 2 }}
                        slotProps={{ input: { startAdornment: <InputAdornment position='start'>#</InputAdornment>, inputProps: { maxLength: 6 } } }}
                    />
                    <Box sx={{ mr: 2, display: 'inline-flex', alignItems: 'center' }}>
                        <Button component="label" variant="outlined">
                            Upload Logo
                            <input
                                type="file"
                                accept="image/*"
                                hidden
                                onChange={handleLogoChange}
                            />
                        </Button>
                        {logoFile && <Typography sx={{ ml: 1 }}>{logoFile.name}</Typography>}
                    </Box>
                    <Button
                        type="submit"
                        variant="contained"
                        loading={loading.updateBrand}
                        loadingIndicator="Updating…"
                    >
                        Update Brand
                    </Button>
                </Box>
                {brand && (
                    <Box sx={{ mt: 2 }}>
                        <Typography>Current Brand Settings:</Typography>
                        <Typography>Color: {brand.color}</Typography>
                        <Box>
                            <Typography>Logo:</Typography>
                            <img
                                src={getLogoUrl() || ''}
                                alt="Brand Logo"
                                style={{ maxWidth: '200px', maxHeight: '100px', marginTop: '8px' }}
                                onError={(e) => (e.currentTarget.style.display = 'none')} // Hide if logo doesn't exist
                            />
                        </Box>
                    </Box>
                )}
            </TabPanel>
        </Box>
    );
}